import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { NegotiateParams } from '../api/session'
import { useBrand } from '../contexts/BrandContext'
import { setSocketId, setWebRTCStatus } from '../store/reducers/sessionReducer'
import { NegotiateSocketMessage } from '../types/socketMessage'
import { WebRTCMessage } from '../types/webRtcMessage'
import { analytics } from '../utils/analytics'
import { MediaCapture } from '../utils/mediaCapture'
import { useNegotiation } from './apiHooks/sessionHooks'
import { useCompositeData, useSession } from './storeHooks/useSessionState'
import { Microphone } from './useMicrophones'

type MessageHandler = (data: WebRTCMessage) => void

interface useSessionWebRTCProps {
  avatarId: string
  userMicrophoneStream: MediaStream | null
  selectedMicrophone: Microphone | null
  mentorId: string
  prevMentorId: string | null
  prevSocketId: string | null
  isAuthenticated: boolean
  profileData?: {
    linkedInSummary: string | null
    profileJson: string | null
  }
  context?: string
  companionSlug?: string
  isInterview?: boolean
  onMessage: MessageHandler
}

const rtcConfig: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun1.ciscape.com:3478' },
    {
      urls: 'turn:stun1.ciscape.com:3478',
      username: 'tcuser',
      credential: 'dw4k_BR2f-Y4A-@aT7ip@Qzs',
    },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
}

let prevMessages: WebRTCMessage[] = []

const getPrevMessages = () => {
  const messages = prevMessages
  prevMessages = []
  return messages
}

export const useSessionWebRTC = ({
  avatarId,
  userMicrophoneStream,
  selectedMicrophone,
  mentorId,
  context,
  prevMentorId,
  prevSocketId,
  companionSlug,
  isAuthenticated,
  profileData,
  onMessage,
  isInterview,
}: useSessionWebRTCProps) => {
  const dispatch = useDispatch()
  const brand = useBrand()
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const [audioReady, setAudioReady] = useState(false)
  const [sessionId, setLocalSessionId] = useState<string | null>(null)
  const openAiSessionRef = useRef<string | null>(null)

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const audioSenderRef = useRef<RTCRtpTransceiver | null>(null)
  const isMounted = useRef(true)
  const socketRef = useRef<WebSocket | null>(null)
  const abortController = useRef(new AbortController())

  const { setDataChannel, setSessionId } = useSession()
  const { setVoicesList } = useCompositeData()
  const screenShareCaptureRef = useRef<MediaCapture | null>(null)

  const { mutateAsync: doNegotiation } = useNegotiation()

  useEffect(() => {
    screenShareCaptureRef.current = new MediaCapture({
      interval: 1000,
    })
  }, [])

  const handleMessage = useCallback(
    (evt: { data: string; type: string }) => {
      const data = JSON.parse(evt.data) as WebRTCMessage
      if (data?.data?.message?.reason === 'content_filter') {
        console.log(
          `%cContent filter ${openAiSessionRef.current ? openAiSessionRef.current : ''}`,
          'color: #0062ff',
        )
        console.log('prev messages:', getPrevMessages())
        analytics.logEvent(
          context
            ? 'watch_session_content_filter_triggered'
            : 'regular_session_content_filter_triggered',
          {
            companion_slug: companionSlug || '',
          },
        )
      }
      if (data?.data?.message?.session?.id) {
        openAiSessionRef.current = data?.data?.message?.session?.id
      }
      onMessage(data)
    },
    [onMessage],
  )

  const setInstructions = (promptData: string) => {
    if (dataChannelRef.current) {
      const dataSettings = {
        type: 'set_settings',
        data: {
          instructions: `The user has provided an additional file containing information: ${JSON.stringify(
            promptData,
          )}`,
          temperature: 1,
        },
      }
      console.log('set_settings', promptData)
      dataChannelRef.current.send(JSON.stringify(dataSettings))
    }
  }

  const handleConnectionStateChange = useCallback(() => {
    if (!pcRef.current) return
    const { connectionState } = pcRef.current
    console.log('Connection state changed:', connectionState)
    switch (connectionState) {
      case 'failed':
        trackError('webrtc')
        dispatch(
          setWebRTCStatus({
            blocked: true,
            reason: 'WebRTC connection failed',
            message:
              'It looks like your network is unstable or blocking WebRTC. Try disabling your VPN or switching to a different network.',
          }),
        )
        break
      case 'connected':
        dispatch(setWebRTCStatus({ blocked: false, reason: '', message: '' }))
        console.log('Closing signaling socket')
        socketRef.current?.close()
        socketRef.current = null
        break
      default:
        break
    }
  }, [dispatch])

  const handleGetTrack = useCallback(
    (evt: RTCTrackEvent) => {
      const stream = evt.streams[0]
      if (evt.track.kind === 'video') {
        console.log('Video track received')
        setVideoStream(stream)
      } else {
        console.log('Audio track received')
        setAudioStream(stream)
      }
    },
    [dispatch],
  )

  const trackError = (reason: string) => {
    analytics.logEvent(
      context ? 'watch_session_failed' : 'regular_session_failed',
      { reason },
    )
  }

  const handleSocketConnection = async () => {
    try {
      console.log('Connecting to signaling server')
      const negotiateParams: NegotiateParams = {
        mentorId,
        useIdleTimeOut: false,
        avatarInfo: {
          id: avatarId,
          useGreenVideo: false,
        },
      }

      if (context) {
        negotiateParams.userContextId = context
      }

      if (prevMentorId) {
        negotiateParams.prevMentorId = prevMentorId
      }

      if (prevSocketId) {
        negotiateParams.prevSocketId = prevSocketId
      }

      if (profileData?.linkedInSummary) {
        negotiateParams.linkedInSummary = profileData.linkedInSummary
      }

      if (profileData?.profileJson) {
        negotiateParams.profileJson = profileData.profileJson
      }

      const { connection, socket_id } = await doNegotiation({
        params: negotiateParams,
        abortSignal: abortController.current.signal,
        isAuthenticated,
        trackError,
        isInterview,
      })

      if (socket_id) {
        dispatch(setSocketId(socket_id))
      }

      console.log('waiting for websocket open')
      const websocket = new WebSocket(
        `${connection.signaling_endpoint}/ws/connections/${connection.id}/signaling`,
      )

      const waitForWebsocketOpen = new Promise((resolve, reject) => {
        websocket.onopen = resolve
        websocket.onclose = reject
        websocket.onerror = () => {
          trackError('webrtc_socket')
          reject(new Error('WebSocket error'))
        }
      })

      try {
        await waitForWebsocketOpen
      } catch {
        trackError('webrtc_socket')
      }
      console.log('websocket connected')

      socketRef.current = websocket

      setSessionId(connection.id)
      setLocalSessionId(connection.id)
      setVoicesList(connection?.avatar?.voices || null)
    } catch (error: unknown) {
      if (
        error &&
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        (error as { message?: string }).message !== 'canceled'
      ) {
        const err = error as {
          response?: { status?: number }
          message?: string
        }
        dispatch(
          setWebRTCStatus({
            errorStatus: err?.response?.status,
            blocked: true,
            reason: `WebRTC negotiation error: ${err?.message}`,
            message: `${brand.brandName} is currently experiencing unprecedented demand, please come back later.`,
          }),
        )
      }
    }
  }

  const sendSignal = (type: unknown, data: unknown) => {
    if (socketRef.current) {
      console.log('Sending signal', type, data)
      socketRef.current.send(
        JSON.stringify({
          type,
          data,
        }),
      )
    }
  }

  const handleSocketMessage = (pc: RTCPeerConnection) => {
    if (socketRef.current) {
      socketRef.current.onmessage = async (event: {
        data: string
        type: string
      }) => {
        if (event.type == 'message') {
          const { data, type } = JSON.parse(
            event.data,
          ) as NegotiateSocketMessage
          switch (type) {
            case 'set_remote_description':
              console.log('Setting remote description', data)
              await pc.setRemoteDescription(data)
              break
            case 'error':
              socketRef.current?.close()
              socketRef.current = null
              trackError('socket')
          }
        }
      }
    }
  }

  const negotiateWithSignaling = (pc: RTCPeerConnection) => {
    pc.onnegotiationneeded = async () => {
      await pc.setLocalDescription()
      sendSignal('set_remote_description', pc.localDescription)
    }

    console.log('Creating offer')
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        sendSignal('add_ice_candidate', { candidate })
      }
    }
  }

  const startWebrtc = async () => {
    if (!isMounted.current) return

    await handleSocketConnection()
    isMounted.current = true

    const pc = new RTCPeerConnection(rtcConfig)
    pcRef.current = pc
    handleSocketMessage(pc)

    pc.addEventListener('connectionstatechange', handleConnectionStateChange)
    pc.addEventListener('track', handleGetTrack)

    const dataChannel = pc.createDataChannel('chat', { ordered: true })
    dataChannelRef.current = dataChannel
    setDataChannel(dataChannel)

    dataChannel.onopen = () => {
      console.log('DataChannel opened, ready for screen sharing commands')
    }
    dataChannel.onclose = () => {
      console.log('DataChannel closed')
    }
    dataChannel.onerror = error => {
      console.error('DataChannel error:', error)
    }
    dataChannel.onmessage = handleMessage

    const audioSendTransceiver = pc.addTransceiver('audio', {
      direction: 'sendrecv',
    })
    audioSenderRef.current = audioSendTransceiver

    const canvas = screenShareCaptureRef.current?.getCanvas()

    if (!canvas) return
    const stream = canvas.captureStream(1)
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream)
    })

    setAudioReady(true)
    if (
      userMicrophoneStream &&
      userMicrophoneStream.getAudioTracks().length > 0
    ) {
      const audioTrack = userMicrophoneStream.getAudioTracks()[0]
      await audioSendTransceiver.sender.replaceTrack(audioTrack)
    }

    pc.addTransceiver('video', { direction: 'recvonly' })

    negotiateWithSignaling(pc)
  }

  const stopWebrtc = () => {
    isMounted.current = false

    abortController.current.abort()
    setDataChannel(null)

    // Close the DataChannel
    dataChannelRef.current?.close()
    if (dataChannelRef.current) {
      dataChannelRef.current.onmessage = null
    }
    dataChannelRef.current = null

    // Close the PeerConnection
    pcRef.current?.close()

    if (pcRef.current) {
      pcRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.stop()
        }
      })

      pcRef.current.removeEventListener(
        'connectionstatechange',
        handleConnectionStateChange,
      )
      pcRef.current.removeEventListener('track', handleGetTrack)
    }
    pcRef.current = null

    socketRef.current?.close()
    socketRef.current = null

    // Reset Redux states
    setVideoStream(null)
    setAudioStream(null)
    dispatch(
      setWebRTCStatus({
        blocked: false,
        reason: '',
        message: '',
      }),
    )
  }

  useEffect(() => {
    if (!avatarId && !isInterview) return
    startWebrtc().catch(error => {
      console.error('Failed to start WebRTC:', error)
      trackError('webrtc')
      dispatch(
        setWebRTCStatus({
          blocked: true,
          reason: 'WebRTC initialization error',
          message:
            'The WebRTC connection could not be established. Please try again later.',
        }),
      )
    })
    return stopWebrtc
  }, [avatarId, isInterview])

  useEffect(() => {
    if (
      pcRef.current &&
      audioSenderRef.current &&
      userMicrophoneStream &&
      audioReady
    ) {
      const newAudioTrack = userMicrophoneStream.getAudioTracks()[0]
      if (newAudioTrack) {
        audioSenderRef.current.sender
          .replaceTrack(newAudioTrack)
          .catch((error: unknown) =>
            console.error('Failed to replace audio track:', error),
          )
      }
    }
  }, [userMicrophoneStream, selectedMicrophone, audioReady])

  return {
    videoStream,
    audioStream,
    sessionId,
    screenShareCaptureRef,
    isWebRtcConnected:
      pcRef.current?.connectionState === 'connected' &&
      dataChannelRef.current?.readyState === 'open',
    stopWebrtc,
    startWebrtc,
    setInstructions,
  }
}
